import React, { FunctionComponent } from 'react'
import { gql, useMutation, useQuery } from '@apollo/client'
import { spaceRoute, spacesRoute } from 'helpers/routing'
import { Col, Row, Form } from 'reactstrap'
import Divider from 'components/common/Divider'
import FormField from 'components/common/FormField'
import Loader from 'components/common/Loader'
import PrimaryButton from 'components/common/PrimaryButton'
import SecondaryButton from 'components/common/SecondaryButton'
import { SpaceCreateInput, Query } from 'generated/types'
import LayoutTitle from 'teleporters/LayoutTitle'
import PageTitle from 'components/common/PageTitle'
import PageBreadcrumbs from 'components/common/PageBreadcrumbs'
import { useHistory } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { SPACES_QUERY } from 'pages/spaces/list'

const LOCATIONS_QUERY = gql`
  query locations {
    locations {
      id
      text
      slug
    }
  }
`

const CREATE_SPACE_MUTATION = gql`
  mutation createOneSpace($input: SpaceCreateInput) {
    createOneSpace(input: $input) {
      id
    }
  }
`

interface CreateSpaceFormData {
  name: string
  capacity: string
  location: string
}

export const CreateSpacePage: FunctionComponent = () => {
  const locationsQuery = useQuery<Query>(LOCATIONS_QUERY)
  const [createSpace] = useMutation(CREATE_SPACE_MUTATION, {
    refetchQueries: [{ query: SPACES_QUERY }],
  })
  const history = useHistory()
  const { handleSubmit, register, errors } = useForm<CreateSpaceFormData>()

  if (locationsQuery.loading) {
    return (
      <Loader />
    )
  }

  const locationEntries = locationsQuery.data!.locations.map((l) => {
    return (
      <option value={l.slug} key={`location-${l.slug}`}>{l.text}</option>
    )
  })

  const onSubmit = async ({ name, capacity, location }:CreateSpaceFormData) => {
    const input: SpaceCreateInput = {
      name,
      capacity: capacity ? parseInt(capacity, 10) : 0,
      location: {
        slug: location,
      },
    }

    const resp = await createSpace({
      variables: { input },
    })

    history.push(spaceRoute(resp.data.createOneSpace.id))
  }

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <LayoutTitle.Source>
        <PageBreadcrumbs routeTo="/spaces">
          Spaces
        </PageBreadcrumbs>
        <PageTitle>Create Space</PageTitle>
      </LayoutTitle.Source>

      <Divider />

      <Row>
        <Col xs="12" sm="12" md="5">
          <FormField
            label="Name"
            name="name"
            innerRef={register({
              required: 'Name is required',
            })}
            feedback={errors.name?.message}
            invalid={Boolean(errors.name)}
          />
          <FormField
            label="Location/Building"
            name="location"
            type="select"
            innerRef={register({
              required: true,
            })}
          >
            {locationEntries}
          </FormField>
          <FormField
            label="Normal Capacity"
            name="capacity"
            pattern="[0-9]*"
            type="number"
            feedback={errors.capacity?.message}
            innerRef={register({
              min: 0,
            })}
            invalid={Boolean(errors.capacity)}
          />
        </Col>
      </Row>

      <Row className="mt-3">
        <Col xs="12">
          <SecondaryButton
            title="Cancel"
            to={spacesRoute()}
            buttonProps={{ className: 'mr-4' }}
          />
          <PrimaryButton
            title="Add Space"
          />
        </Col>
      </Row>
    </Form>
  )
}

export default CreateSpacePage
